/**
 * /verify — screen 3w. Waiting on the email.
 *
 * Three things can finish this screen, and all three are wired:
 *
 *  1. The link, opened **here** — `?token=` completes on mount.
 *  2. The link, opened **somewhere else** — Gmail's in-app browser, a laptop.
 *     That is why this screen polls `GET /api/state` every three seconds: the
 *     tab the user is staring at has to move when the tap happened elsewhere.
 *  3. The six-digit code (D6), which exists precisely because a mail client's
 *     webview does not share cookies with Safari and kills path 1 outright.
 *
 * The code field is a real OTP field, not six decorative boxes: numeric
 * keyboard, `one-time-code` autofill, auto-advance, backspace to the previous
 * box, and a paste that fills all six at once.
 *
 * D5's race — the name got claimed while the mail sat unread — comes back as a
 * 409 `name_taken`, and the only sane destination for that is the D115 picker.
 */

import { useCallback, useEffect, useRef, useState, type ClipboardEvent as ReactClipboardEvent } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import type { AppState } from '@shared/types'
import { useStore } from '../lib/store'
import { ApiFailure, GET, POST } from '../lib/api'
import { Notice, Screen, Spacer } from '../ui'

const RESEND_SECONDS = 60
const POLL_MS = 3000

type Mode = 'verify' | 'signin'

interface VerifyNavState { mode?: Mode; masked?: string; email?: string; name?: string }

function readSession(key: string): string | null {
  try { return sessionStorage.getItem(key) } catch { return null }
}

function mmss(total: number): string {
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

export default function Verify() {
  const navigate = useNavigate()
  const location = useLocation()
  const [params] = useSearchParams()
  const { refresh } = useStore()

  const nav = (location.state ?? null) as VerifyNavState | null

  const mode: Mode = nav?.mode
    ?? ((readSession('sfg.verifyMode') === 'signin') ? 'signin' : 'verify')
  const masked = nav?.masked ?? readSession('sfg.maskedEmail')
  const signinName = nav?.name ?? readSession('sfg.signinName') ?? ''
  const resendEmail = nav?.email ?? null

  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', ''])
  const [active, setActive] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [left, setLeft] = useState(RESEND_SECONDS)
  const [resent, setResent] = useState(false)

  const boxes = useRef<(HTMLInputElement | null)[]>([])
  const finished = useRef(false)

  // ── finishing ─────────────────────────────────────────────────────────────

  const finish = useCallback(async () => {
    if (finished.current) return
    finished.current = true
    await refresh()
    navigate('/', { replace: true })
  }, [navigate, refresh])

  const complete = useCallback(async (body: Record<string, unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await POST(mode === 'signin' ? '/api/auth/signin/complete' : '/api/auth/verify/complete', body)
      try { sessionStorage.removeItem('sfg.verifyMode') } catch { /* private mode */ }
      await finish()
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'name_taken') {
        // D5 step 2 failed: the email is proven, the name is not (D115).
        finished.current = true
        navigate('/claim-name', { replace: true })
        return
      }
      setError(e instanceof ApiFailure ? e.message : 'That code didn’t work.')
      setDigits(['', '', '', '', '', ''])
      setActive(0)
      boxes.current[0]?.focus()
    } finally {
      setBusy(false)
    }
  }, [finish, mode, navigate])

  // ── the link, opened here ─────────────────────────────────────────────────

  const token = params.get('token')
  useEffect(() => {
    if (!token) return
    void complete({ token })
    // `complete` is stable enough; re-running on a token that never changes
    // would double-burn it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // ── the link, opened somewhere else: poll (D6) ────────────────────────────

  useEffect(() => {
    if (token) return
    let stop = false
    const tick = async () => {
      if (stop || finished.current) return
      try {
        const state = await GET<AppState>('/api/state')
        if (stop || finished.current) return
        if (mode === 'signin' || state.user.isVerified) void finish()
      } catch (e) {
        if (e instanceof ApiFailure && e.code === 'name_pending') {
          finished.current = true
          navigate('/claim-name', { replace: true })
        }
        // unauthenticated / offline: keep waiting, the screen is the wait.
      }
    }
    const t = setInterval(() => { void tick() }, POLL_MS)
    return () => { stop = true; clearInterval(t) }
  }, [finish, mode, navigate, token])

  // ── resend countdown ──────────────────────────────────────────────────────

  useEffect(() => {
    if (left <= 0) return
    const t = setTimeout(() => setLeft((n) => n - 1), 1000)
    return () => clearTimeout(t)
  }, [left])

  const resend = useCallback(async () => {
    setError(null)
    try {
      if (mode === 'signin') {
        await POST('/api/auth/signin/start', { name: signinName })
      } else if (resendEmail) {
        await POST('/api/auth/verify/start', { email: resendEmail })
      } else {
        setError('Open Settings and enter your email again to get a fresh link.')
        return
      }
      setResent(true)
      setLeft(RESEND_SECONDS)
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t send another one.')
    }
  }, [mode, resendEmail, signinName])

  // ── OTP field behaviour ───────────────────────────────────────────────────

  const commit = useCallback((next: string[]) => {
    setDigits(next)
    if (next.every((d) => d !== '')) {
      const code = next.join('')
      void complete(mode === 'signin' ? { name: signinName, code } : { code })
    }
  }, [complete, mode, signinName])

  const onDigit = useCallback((i: number, raw: string) => {
    const only = raw.replace(/\D/g, '')
    if (!only) {
      const next = [...digits]
      next[i] = ''
      setDigits(next)
      return
    }
    if (only.length > 1) {
      // A keyboard that delivers the whole code into one box.
      const next = [...digits]
      for (let k = 0; k < only.length && i + k < 6; k++) next[i + k] = only[k]
      const landed = Math.min(5, i + only.length)
      setActive(landed)
      boxes.current[landed]?.focus()
      commit(next)
      return
    }
    const next = [...digits]
    next[i] = only
    if (i < 5) {
      setActive(i + 1)
      boxes.current[i + 1]?.focus()
    }
    commit(next)
  }, [commit, digits])

  const onKeyDown = useCallback((i: number, key: string) => {
    if (key === 'Backspace' && digits[i] === '' && i > 0) {
      const next = [...digits]
      next[i - 1] = ''
      setDigits(next)
      setActive(i - 1)
      boxes.current[i - 1]?.focus()
    } else if (key === 'ArrowLeft' && i > 0) {
      setActive(i - 1)
      boxes.current[i - 1]?.focus()
    } else if (key === 'ArrowRight' && i < 5) {
      setActive(i + 1)
      boxes.current[i + 1]?.focus()
    }
  }, [digits])

  const onPaste = useCallback((e: ReactClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!text) return
    e.preventDefault()
    const next = ['', '', '', '', '', '']
    for (let i = 0; i < text.length; i++) next[i] = text[i]
    const landed = Math.min(5, text.length)
    setActive(landed)
    boxes.current[landed]?.focus()
    commit(next)
  }, [commit])

  return (
    <Screen style={{ justifyContent: 'center', padding: '0 24px' }}>
      <main style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, margin: 0, letterSpacing: '-0.02em' }}>
          Check your email
        </h1>
        <p style={{ fontSize: 14, color: 'var(--dim)', margin: '10px 0 0', lineHeight: 1.5 }}>
          {masked
            ? <>We sent a link to {masked}.</>
            : mode === 'signin'
              ? <>If that name has an email locked to it, a link is on its way.</>
              : <>We sent you a link.</>}
        </p>

        {error ? <><Spacer h={16} /><Notice tone="alert">{error}</Notice></> : null}
        {resent && !error ? <><Spacer h={16} /><Notice tone="info">Sent another one.</Notice></> : null}

        <label
          htmlFor="otp-0"
          style={{ display: 'block', fontSize: 13, color: 'var(--dim)', marginTop: 24 }}
        >
          Or enter the 6-digit code:
        </label>

        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8, marginTop: 10 }}
          aria-busy={busy}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              id={`otp-${i}`}
              ref={(el) => { boxes.current[i] = el }}
              value={d}
              onChange={(e) => onDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e.key)}
              onPaste={onPaste}
              onFocus={() => setActive(i)}
              inputMode="numeric"
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              maxLength={6}
              disabled={busy}
              aria-label={`Digit ${i + 1} of 6`}
              className="mono"
              style={{
                aspectRatio: '.8', width: '100%', textAlign: 'center', padding: 0,
                borderRadius: 10, fontSize: 18, color: 'var(--fg)',
                background: d || active === i ? 'var(--surf)' : 'transparent',
                border: `1px solid ${active === i || d ? 'var(--fill)' : 'var(--line)'}`,
              }}
            />
          ))}
        </div>

        <div className="mono" style={{ fontSize: 12, color: 'var(--dim)', marginTop: 18 }}>
          {left > 0 ? (
            <>Didn’t get it? Resend in {mmss(left)}</>
          ) : (
            <button
              onClick={() => { void resend() }}
              className="mono"
              style={{ fontSize: 12, color: 'var(--bright)', fontWeight: 600, minHeight: 44 }}
            >
              Didn’t get it? Send another →
            </button>
          )}
        </div>

        <p style={{
          marginTop: 30, paddingTop: 20, borderTop: '1px solid var(--line)',
          fontSize: 12, color: 'var(--dim)', lineHeight: 1.6,
        }}>
          We use your email for three things and nothing else: this link, signing you back in if
          you lose your session, and one question on Fridays about whether you’re coming.
        </p>
      </main>
    </Screen>
  )
}
